/**
 * GatePassLabel — A5 printable label sheet stitching the server-side QR
 * (via /mtx/qr/{payload}) + JsBarcode-rendered barcodes for each RFID tag
 * on a Gate Pass. Opens in a new window with a print-optimised stylesheet
 * so Security can hand the driver a physical pass at the WH exit gate.
 *
 * Consumed by MirCard and GatePassRegister via a "Print" button.
 */
import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import api from '@/lib/api';
import { X, Printer, Loader2 } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)' };

export default function GatePassLabel({ projectId, gatePass, mir, onClose }) {
  const rootRef = useRef(null);

  // Render RFID barcodes into every <svg data-rfid=...> in the sheet.
  useEffect(() => {
    if (!gatePass) return;
    const rfids = gatePass.rfidTagList || [];
    // Fallback: if no RFIDs are stored, at least barcode the GP code itself
    const codes = rfids.length > 0 ? rfids : [gatePass.code || gatePass.id];
    setTimeout(() => {
      codes.forEach((code, i) => {
        const el = rootRef.current?.querySelector(`svg[data-rfid="${i}"]`);
        if (el) {
          try {
            JsBarcode(el, code, {
              format: 'CODE128', width: 1.5, height: 40,
              displayValue: true, fontSize: 10, margin: 0,
            });
          } catch { /* invalid RFID — skip */ }
        }
      });
    }, 30);
  }, [gatePass]);

  if (!gatePass) return null;

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const qrUrl = `${backendBase}/api/v1/projects/${projectId}/mtx/qr/${encodeURIComponent(gatePass.qrPayload || gatePass.code)}?size=10`;
  const rfids = gatePass.rfidTagList || [];
  const codes = rfids.length > 0 ? rfids : [gatePass.code || gatePass.id];
  const lineItems = mir?.lineItems || [];

  const printSheet = () => {
    const html = rootRef.current?.querySelector('.print-sheet')?.outerHTML || '';
    const win = window.open('', '_blank', 'width=800,height=1100');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Gate Pass ${gatePass.code}</title>
      <style>
        @page { size: A5; margin: 8mm; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 0; color: #0F172A; }
        .print-sheet { width: 138mm; padding: 4mm; }
        .header { border-bottom: 2px solid #1E4E8C; padding-bottom: 3mm; margin-bottom: 4mm; }
        .brand { font-size: 9pt; color: #64748B; text-transform: uppercase; letter-spacing: 1px; }
        .title { font-size: 20pt; font-weight: 900; color: #1E4E8C; margin: 1mm 0 0; }
        .subtitle { font-size: 9pt; color: #64748B; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4mm; margin-bottom: 4mm; }
        .cell { border: 1px solid rgba(15,23,42,0.12); border-radius: 2mm; padding: 3mm; }
        .cell-label { font-size: 7pt; color: #64748B; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 1mm; }
        .cell-value { font-size: 10pt; font-weight: 700; color: #0F172A; }
        .qr-block { text-align: center; margin: 5mm 0; }
        .qr-block img { width: 60mm; height: 60mm; border: 1px solid rgba(15,23,42,0.15); border-radius: 2mm; padding: 2mm; }
        .qr-code { font-family: monospace; font-size: 10pt; font-weight: 700; margin-top: 2mm; color: #F26B1F; }
        .rfid-list { margin-top: 4mm; }
        .rfid-list h3 { font-size: 8pt; color: #64748B; text-transform: uppercase; margin: 0 0 2mm; letter-spacing: 0.5px; }
        .rfid-row { border-bottom: 1px dashed rgba(15,23,42,0.10); padding: 1mm 0; text-align: center; }
        .rfid-row svg { max-width: 100%; }
        .li-table { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 8.5pt; }
        .li-table th { text-align: left; background: #F8FAFC; padding: 1.5mm; color: #64748B; font-size: 7pt; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid rgba(15,23,42,0.10); }
        .li-table td { padding: 1.5mm; border-bottom: 1px solid rgba(15,23,42,0.08); }
        .sign-row { margin-top: 6mm; display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; font-size: 8pt; color: #64748B; }
        .sign-cell { border-top: 1px solid #0F172A; padding-top: 1.5mm; text-align: center; }
        .footer { margin-top: 6mm; text-align: center; font-size: 7pt; color: #94A3B8; border-top: 1px solid rgba(15,23,42,0.10); padding-top: 2mm; }
        .badge { display: inline-block; padding: 0.5mm 2mm; border-radius: 3mm; background: #FEF3C7; color: #92400E; font-size: 8pt; font-weight: 700; }
        @media print { .no-print { display: none !important; } }
      </style>
    </head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div ref={rootRef} className="bg-white rounded-lg overflow-y-auto shadow-2xl"
        style={{ maxWidth: 620, width: '100%', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()} data-testid="gate-pass-label">

        {/* Toolbar (excluded from print) */}
        <div className="flex items-center justify-between p-3 border-b no-print"
          style={{ borderColor: C.border }}>
          <div className="text-sm font-bold" style={{ color: C.slate }}>
            Gate Pass Label · A5 Printable
          </div>
          <div className="flex gap-2">
            <button onClick={printSheet}
              data-testid="gate-pass-print"
              className="px-3 py-1.5 rounded text-[11px] font-bold flex items-center gap-1"
              style={{ background: C.accent, color: '#fff' }}>
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose} data-testid="gate-pass-close"
              className="px-2 py-1.5 rounded" style={{ background: '#F1F5F9' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* The A5 sheet — same markup goes to the print window */}
        <div className="print-sheet" style={{ padding: '4mm' }}>
          <div className="header">
            <div className="brand">Panorix · Material Management</div>
            <div className="title">Gate Pass</div>
            <div className="subtitle">
              Present to Security at the WH Exit &amp; Site Entry gates ·
              scan QR + RFID at every checkpoint
            </div>
          </div>

          <div className="grid">
            <div className="cell">
              <div className="cell-label">Gate Pass Code</div>
              <div className="cell-value" style={{ color: C.accent, fontFamily: 'monospace' }}>{gatePass.code || gatePass.id}</div>
            </div>
            <div className="cell">
              <div className="cell-label">MIR</div>
              <div className="cell-value">{mir?.code || gatePass.mirId || '—'}</div>
            </div>
            <div className="cell">
              <div className="cell-label">Warehouse</div>
              <div className="cell-value">{mir?.warehouseLocation || '—'}</div>
            </div>
            <div className="cell">
              <div className="cell-label">Workfront / WBS</div>
              <div className="cell-value">{mir?.workfront || '—'}{mir?.wbsActivityId ? ` · ${mir.wbsActivityId}` : ''}</div>
            </div>
            <div className="cell">
              <div className="cell-label">Units on Pass</div>
              <div className="cell-value">{(gatePass.materialUnitIds || []).length}</div>
            </div>
            <div className="cell">
              <div className="cell-label">Issued At · TTL</div>
              <div className="cell-value" style={{ fontSize: '8pt' }}>
                {gatePass.issuedAt ? new Date(gatePass.issuedAt).toLocaleString() : '—'}<br/>
                <span className="badge">TTL {gatePass.ttlBetweenScansMinutes || 240} min</span>
              </div>
            </div>
          </div>

          {/* Main QR */}
          <div className="qr-block">
            <img src={qrUrl} alt={`QR ${gatePass.code}`} data-testid="gate-pass-qr" />
            <div className="qr-code">{gatePass.qrPayload || gatePass.code}</div>
          </div>

          {/* Line items */}
          {lineItems.length > 0 && (
            <table className="li-table">
              <thead>
                <tr><th>MCI Line</th><th>Qty</th><th>Bin</th></tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace' }}>{li.mciLineNumber}</td>
                    <td>{li.qty}</td>
                    <td>{li.binLocation || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* RFID barcodes */}
          <div className="rfid-list">
            <h3>RFID Tags ({codes.length})</h3>
            {codes.map((c, i) => (
              <div key={i} className="rfid-row">
                <svg data-rfid={i} />
              </div>
            ))}
          </div>

          {/* Signatures */}
          <div className="sign-row">
            <div className="sign-cell">Storekeeper · Sign / Stamp</div>
            <div className="sign-cell">Security · Sign / Stamp</div>
          </div>

          <div className="footer">
            Panorix Gate Pass · valid only for the MIR &amp; materials listed above ·
            any tampering voids this pass · counter-verify RFID scan at every gate
          </div>
        </div>
      </div>
    </div>
  );
}
